import { FC, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEvent, trackClick, useAnalytics } from '@bonusly/analytics';
import { RichTextDisplay, RichTextInput, useRichText } from '@bonusly/common-rich-text-editor';
import {
	Avatar,
	Box,
	Button,
	Center,
	chakra,
	Divider,
	Dropdown,
	DropdownInput,
	DropdownMenu,
	DropdownOption,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Heading,
	HStack,
	Image,
	Input,
	Radio,
	RadioGroup,
	rem,
	SimpleGrid,
	Spacer,
	Stack,
	Text,
	Textarea,
	useDisclosure,
	XOutline,
} from '@bonusly/design-system';

import avatarURL from '../../../assets/images/empty-avatar.png';
import { ReactComponent as Icon } from '../../../assets/images/ribbon-icon.svg';
import { Reveal } from '../../../components/display/reveal';
import { CountHelpText } from '../../../components/form/count-help-text';
import { FormErrorMessage } from '../../../components/form/form-error-message';
import { FormSection } from '../../../components/form/form-section';
import { InfoLabel } from '../../../components/form/info-label';
import { RadioChip, RadioChipGroup } from '../../../components/form/radio-chip';
import { ImageUploadInput } from '../../../components/form/upload/image-upload-input';
import { ReactComponent as NoImageIcon } from '../../../components/incentives/assets/no-image-icon.svg';
import { AvatarGroup } from '../../../design-system/avatar/components/avatar-group';
import { GroupType } from '../../../schemas/groups';
import { ClaimLimitPeriod, IncentiveVisibility } from '../../../schemas/incentives';
import { generateFileURL } from '../../../utils/file';
import { ClaimLimitMode, IncentiveFormData } from '../types';
import { ApprovalProcess } from './approval-process';
import { AudienceSelector } from './audience-selector';
import { BudgetEstimateEmployee } from './budget-estimate-employee';
import { BudgetEstimatePeriod } from './budget-estimate-period';
import { EligibilityCount } from './eligibility-count';

type IncentiveFormProps = {
	children?: ReactNode;
	form: ReturnType<typeof useForm<IncentiveFormData>>;
	isLoading: boolean;
	onSubmit: (data: IncentiveFormData) => void;
};

const RibbonIcon = chakra(Icon);

export const IncentiveForm: FC<IncentiveFormProps> = ({ children, form, onSubmit }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });
	const { track } = useAnalytics();
	const { t: tPrev } = useTranslation('incentives', { keyPrefix: 'recentlyClaimed' });
	const claimPreview = useDisclosure();
	const feedPreview = useDisclosure();

	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
		setValue,
		watch,
	} = form;

	const [
		icon,
		conditions,
		conditionsOperator,
		claimLimitMode,
		periodType,
		claimLimitQuantity,
		amount,
		userClaimLimit,
		requireApproval,
		name,
		description,
		visibility,
	] = watch([
		'icon',
		'conditions',
		'conditionsOperator',
		'claimLimitMode',
		'periodType',
		'claimLimitQuantity',
		'amount',
		'userClaimLimit',
		'requireApproval',
		'name',
		'description',
		'visibility',
	]);

	const iconURL = generateFileURL(icon);

	const { editor } = useRichText({
		content: form.getValues('description'),
		onUpdate: ({ editor }) => {
			setValue('description', editor?.getHTML() ?? '', { shouldValidate: true, shouldDirty: true });
		},
	});

	const visibilityHelperText = (() => {
		switch (visibility) {
			case IncentiveVisibility.Public:
				return t('visibilityHelperPublic');
			case IncentiveVisibility.Team:
				return t('visibilityHelperTeam');
			case IncentiveVisibility.Private:
				return t('visibilityHelperPrivate');
			default:
				return '';
		}
	})();

	return (
		<Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={6}>
			<FormSection>
				<Heading size="xl">{t('basicSection')}</Heading>

				<FormControl isInvalid={!!errors.name}>
					<FormLabel>{t('nameLabel')}</FormLabel>
					<Input type="text" {...register('name')} />
					<CountHelpText count={name.length} error={errors.name?.message} max={40}>
						{t('nameHelperText')}
					</CountHelpText>
				</FormControl>

				<FormControl>
					<FormLabel>{t('imageLabel')}</FormLabel>
					<Controller
						control={control}
						name="icon"
						render={({ field }) => (
							<ImageUploadInput {...field} size={rem(180)} value={iconURL} fallback={<NoImageIcon />}>
								<FormHelperText my={0}>{t('imageHelperText')}</FormHelperText>
							</ImageUploadInput>
						)}
					/>
				</FormControl>

				<FormControl isInvalid={!!errors.description}>
					<FormLabel>{t('descriptionLabel')}</FormLabel>
					<RichTextInput editor={editor} ref={register('description').ref} data-testid="description-input" />
					<CountHelpText count={editor?.getText().trim().length ?? 0} error={errors.description?.message} max={500}>
						{t('descriptionHelperText')}
					</CountHelpText>
				</FormControl>

				<FormControl>
					<FormLabel>{t('requireProofLabel')}</FormLabel>
					<FormHelperText my={0} mb={2}>
						{t('requireProofHelperText')}
					</FormHelperText>

					<Controller
						control={control}
						name="requireProof"
						render={({ field }) => (
							<RadioGroup
								{...field}
								onChange={(value) => {
									field.onChange(value === 'true');
									track(AnalyticsEvent.AdminIncentives.RequireProof, { requireProof: value === 'true' });
								}}
								value={field.value.toString()}
								{...trackClick(AnalyticsEvent.AdminIncentives.RequireProof)}
							>
								<Stack>
									<Radio value="true">{t('requireProofEnabled')}</Radio>
									<Radio value="false">{t('requireProofDisabled')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Reveal if={claimPreview.isOpen}>
					<Divider />

					<Text size="sm">{t('claimPreviewDescription')}</Text>

					<Box bgColor="secondary.bgAlpha" border="1px solid #6B74853D" borderRadius="md" pt={5} px={5}>
						<Box maxW={rem(480)} margin="auto">
							<HStack p={4} borderTopRadius="sm" bgColor="bg.surface" borderColor="secondary.bgAlpha" borderBottomWidth="1px" borderStyle="solid">
								<Heading size="3xl">{t('claimPreviewTitle')}</Heading>
								<Spacer />
								<Button colorScheme="secondary" variant="alpha" boxSize={rem(40)} display="flex" flexShrink={0} p={0} borderRadius="full">
									<XOutline size="sm" />
								</Button>
							</HStack>

							<Flex bgColor="bg.surface" p={4} columnGap={3}>
								{iconURL && (
									<Center boxSize={rem(135)} overflow="hidden" flexShrink={0}>
										<Image src={iconURL} w="full" h="full" objectFit="cover" />
									</Center>
								)}

								<Box flexGrow={1} pt={1.5}>
									<Text fontWeight="semibold" mb={1}>
										{name}
									</Text>
									<RichTextDisplay>{description}</RichTextDisplay>
								</Box>
							</Flex>
						</Box>
					</Box>
				</Reveal>

				<HStack>
					<Button onClick={claimPreview.onToggle} variant="ghost" {...trackClick(AnalyticsEvent.AdminIncentives.BasicInfoPreview)}>
						{t('previewLabel', { context: claimPreview.isOpen ? 'open' : 'closed' })}
					</Button>
				</HStack>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('eligibilitySection')}</Heading>

				<FormControl isInvalid={!!errors.conditions}>
					<FormLabel>{t('audienceSectionLabel')}</FormLabel>
					<FormHelperText my={0} mb={2}>
						{t('audienceHelper')}
					</FormHelperText>

					<EligibilityCount conditions={conditions} operator={conditionsOperator} />

					<Reveal if={conditions.length > 1}>
						<Controller
							control={control}
							name="conditionsOperator"
							render={({ field }) => (
								<RadioChipGroup onChange={field.onChange} value={field.value} mb={2}>
									<RadioChip value={GroupType.And}>{t('audienceAnd')}</RadioChip>
									<RadioChip value={GroupType.Or}>{t('audienceOr')}</RadioChip>
								</RadioChipGroup>
							)}
						/>
					</Reveal>

					<Reveal if={conditions.length > 0}>
						<Controller
							control={control}
							name="conditions"
							render={({ field, formState }) => <AudienceSelector error={formState.errors.conditions?.message} onChange={field.onChange} value={field.value} />}
						/>
					</Reveal>

					<Reveal if={conditions.length === 0}>
						<Button
							onClick={() => setValue('conditions', [{ attr: undefined, value: undefined }])}
							{...trackClick(AnalyticsEvent.AdminIncentives.FilterAudienceButton)}
							size="sm"
						>
							{t('audienceEnable')}
						</Button>
					</Reveal>
				</FormControl>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('pointsSection')}</Heading>

				<FormControl>
					<FormLabel mb={2}>{t('claimLimitModeLabel')}</FormLabel>

					<Controller
						control={control}
						name="claimLimitMode"
						render={({ field }) => (
							<RadioGroup
								value={field.value}
								onChange={(value) => {
									field.onChange(value);
									track(AnalyticsEvent.AdminIncentives.SetPointsBudget, { claimLimitMode: value });
								}}
							>
								<Stack spacing={2}>
									<Radio value={ClaimLimitMode.Unlimited}>{t('claimLimitModeUnlimited')}</Radio>
									<Radio value={ClaimLimitMode.PerEmployee}>{t('claimLimitModePerEmployee')}</Radio>
									<Radio value={ClaimLimitMode.PerPeriod}>{t('claimLimitModePerPeriod')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<FormControl isInvalid={!!errors.amount}>
					<InfoLabel label={t('budgetAmountTooltip')}>{t('budgetAmountLabel')}</InfoLabel>
					<Input {...register('amount', { valueAsNumber: true })} />
					<FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
				</FormControl>

				<Reveal if={claimLimitMode === ClaimLimitMode.PerEmployee}>
					<FormControl isInvalid={!!errors.userClaimLimit}>
						<FormLabel mb={2}>{t('userQuantityLabel')}</FormLabel>

						<SimpleGrid columns={2}>
							<Input {...register('userClaimLimit', { valueAsNumber: true })} flex={1} flexBasis="100%" borderEndRadius="none" borderEndColor="transparent" />
							<Controller
								control={control}
								name="periodType"
								render={({ field }) => (
									<Dropdown>
										<Dropdown
											onChange={field.onChange}
											placeholder={t('audienceAttrPlaceholder')}
											renderOption={(value) => t('periodLabel', { context: value })}
											value={field.value}
										>
											<DropdownInput flex={1} flexBasis="100%" borderStartRadius="none" />
											<DropdownMenu>
												<DropdownOption value={ClaimLimitPeriod.Week}>{t('periodLabel', { context: 'week' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Month}>{t('periodLabel', { context: 'month' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Quarter}>{t('periodLabel', { context: 'quarter' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Year}>{t('periodLabel', { context: 'year' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Lifetime}>{t('periodLabel', { context: 'lifetime' })}</DropdownOption>
											</DropdownMenu>
										</Dropdown>
									</Dropdown>
								)}
							/>
						</SimpleGrid>
						<FormErrorMessage>{errors.userClaimLimit?.message}</FormErrorMessage>
						<FormHelperText>{t('userQuantityHelper')}</FormHelperText>
					</FormControl>

					<BudgetEstimateEmployee amount={amount} conditions={conditions} operator={conditionsOperator} period={periodType} userClaimLimit={userClaimLimit} />
				</Reveal>

				<Reveal if={claimLimitMode === ClaimLimitMode.PerPeriod}>
					<FormControl isInvalid={!!errors.claimLimitQuantity}>
						<FormLabel mb={2}>{t('periodQuantityLabel')}</FormLabel>

						<SimpleGrid columns={2}>
							<Input
								{...register('claimLimitQuantity', { valueAsNumber: true })}
								flex={1}
								flexBasis="100%"
								borderEndRadius="none"
								borderEndColor="transparent"
							/>
							<Controller
								control={control}
								name="periodType"
								render={({ field }) => (
									<Dropdown>
										<Dropdown
											onChange={field.onChange}
											placeholder={t('audienceAttrPlaceholder')}
											renderOption={(value) => t('periodLabel', { context: value })}
											value={field.value}
										>
											<DropdownInput flex={1} flexBasis="100%" borderStartRadius="none" />
											<DropdownMenu>
												<DropdownOption value={ClaimLimitPeriod.Week}>{t('periodLabel', { context: 'week' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Month}>{t('periodLabel', { context: 'month' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Quarter}>{t('periodLabel', { context: 'quarter' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Year}>{t('periodLabel', { context: 'year' })}</DropdownOption>
												<DropdownOption value={ClaimLimitPeriod.Lifetime}>{t('periodLabel', { context: 'lifetime' })}</DropdownOption>
											</DropdownMenu>
										</Dropdown>
									</Dropdown>
								)}
							/>
						</SimpleGrid>
						<FormErrorMessage>{errors.claimLimitQuantity?.message}</FormErrorMessage>
						<FormHelperText>{t('periodQuantityHelper')}</FormHelperText>
					</FormControl>

					<FormControl isInvalid={!!errors.userClaimLimit}>
						<InfoLabel isOptional>{t('userQuantityLabel')}</InfoLabel>
						<Input {...register('userClaimLimit', { valueAsNumber: true })} />
						<FormErrorMessage>{errors.userClaimLimit?.message}</FormErrorMessage>
						<FormHelperText>{t('userClaimLimitHelper')}</FormHelperText>
					</FormControl>

					<BudgetEstimatePeriod
						amount={amount}
						conditions={conditions}
						operator={conditionsOperator}
						period={periodType}
						quantity={claimLimitQuantity}
						userClaimLimit={userClaimLimit}
					/>
				</Reveal>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('postsSection')}</Heading>

				<FormControl isInvalid={!!errors.message}>
					<FormLabel>{t('messageLabel')}</FormLabel>
					<Textarea {...register('message')} />
					<FormErrorMessage>{errors.message?.message}</FormErrorMessage>
					<FormHelperText>{t('messageHelper')}</FormHelperText>
				</FormControl>

				<FormControl isInvalid={!!errors.visibility}>
					<FormLabel>{t('visibilityLabel')}</FormLabel>
					<Controller
						control={control}
						name="visibility"
						render={({ field }) => (
							<Dropdown placement="bottom-start" onChange={field.onChange} value={field.value} valueToString={(value: string) => t(`visibility_${value}`)}>
								<DropdownInput w="full" />

								<DropdownMenu>
									<DropdownOption value={IncentiveVisibility.Public}>{t('visibility_publicly_visible')}</DropdownOption>
									<DropdownOption value={IncentiveVisibility.Team}>{t('visibility_immediate_team')}</DropdownOption>
									<DropdownOption value={IncentiveVisibility.Private}>{t('visibility_privately_visible')}</DropdownOption>
								</DropdownMenu>
							</Dropdown>
						)}
					/>
					<FormHelperText mt={1}>{visibilityHelperText}</FormHelperText>
				</FormControl>

				<Reveal if={feedPreview.isOpen}>
					<Divider />

					<Text size="sm">{t('feedPreviewDescription')}</Text>

					<Box bgColor="secondary.bgAlpha" borderRadius="lg" py={5} border="1px solid #6B74853D">
						<HStack px={5} mb={4}>
							<RibbonIcon transform="rotate(-8deg)" />
							<Heading size="lg">{tPrev('title')}</Heading>
							<Spacer />
							<Button variant="ghost">{tPrev('viewAllLabel')}</Button>
						</HStack>

						<Flex columnGap={4} overflow="hidden" px={5} zIndex={0}>
							<Box p={4} bgColor="bg.surface" borderRadius="md" minH={rem(208)} w={rem(200)} flexShrink={0} position="relative">
								<Text fontSize="sm" color="text.softest" mb={1}>
									{tPrev('day')}
								</Text>

								<Text noOfLines={4}>
									<Trans
										t={tPrev}
										i18nKey="userCount"
										values={{ count: 8, context: 'others', name }}
										components={{ '3': <Box as="a" color="text.interactive" cursor="pointer" /> }}
									/>
								</Text>

								<AvatarGroup max={3} position="absolute" bottom={4}>
									{Array.from({ length: 8 }, (_, index) => (
										<Avatar key={index} src={avatarURL} />
									))}
								</AvatarGroup>
							</Box>

							{Array.from({ length: 2 }).map((_, index) => (
								<Flex key={index} flexDir="column" p={4} bgColor="bg.surface" borderRadius="md" minH={rem(208)} w={rem(200)} flexShrink={0} position="relative">
									<Box my={1.5} w="50%" borderRadius={rem(4)} h={rem(12)} bg="linear-gradient(90deg, #E8E9EE 0%, #F0F0F4 100%)"></Box>
									<Box my={1.5} borderRadius={rem(4)} h={rem(12)} bg="linear-gradient(90deg, #E8E9EE 0%, #F0F0F4 100%)"></Box>
									<Box my={1.5} borderRadius={rem(4)} h={rem(12)} bg="linear-gradient(90deg, #E8E9EE 0%, #F0F0F4 100%)"></Box>
									<Box my={1.5} w="50%" borderRadius={rem(4)} h={rem(12)} bg="linear-gradient(90deg, #E8E9EE 0%, #F0F0F4 100%)"></Box>
									<Spacer />
									<Box boxSize={rem(40)} borderRadius="full" bg="linear-gradient(90deg, #E8E9EE 0%, #F0F0F4 100%)" />
								</Flex>
							))}
						</Flex>
					</Box>
				</Reveal>

				<HStack>
					<Button onClick={feedPreview.onToggle} variant="ghost" {...trackClick(AnalyticsEvent.AdminIncentives.RecognitionPostPreview)}>
						{t('previewLabel', { context: feedPreview.isOpen ? 'open' : 'closed' })}
					</Button>
				</HStack>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('approvalSection')}</Heading>

				<FormControl>
					<FormLabel>{t('approvalLabel')}</FormLabel>
					<Controller
						control={control}
						name="requireApproval"
						render={({ field }) => (
							<RadioGroup
								value={field.value.toString()}
								onChange={(value) => {
									field.onChange(value === 'true');
									track(AnalyticsEvent.AdminIncentives.SetApprovalProcess, { requireApproval: value === 'true' });
								}}
								mb={2}
							>
								<Stack spacing={2}>
									<Radio value="false">{t('approvalNotRequired')}</Radio>
									<Radio value="true">{t('approvalRequired')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Reveal if={requireApproval}>
					<FormControl isInvalid={!!errors.approvalLevels}>
						<Controller
							control={control}
							name="approvalLevels"
							render={({ field }) => <ApprovalProcess error={errors.approvalLevels?.message} onChange={field.onChange} value={field.value} />}
						/>
					</FormControl>
				</Reveal>
			</FormSection>

			{children}
		</Stack>
	);
};
